import {BulkDeleteCrownAction} from "@/components/bulkDeleteCrownAction/BulkDeleteCrownAction.tsx";
import {CheckMarkIcon} from "@/components/checkMarkIcon/CheckMarkIcon.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {useFilteredRowSelection} from "@/components/table/hooks/useFilteredRowSelection.ts";
import {TableEmptyStateCta} from "@/components/table/subComponents/TableEmptyStateCta/TableEmptyStateCta.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec} from "@/components/table/Table.types.ts";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {useAutoThrowSuspenseQuery} from "@/lib/hooks/useAutoThrowSuspenseQuery";
import {usePagination} from "@/lib/hooks/usePagination.ts";
import {GQL_SCHEDULED_REPORTS, ScheduledReport, ScheduleReportsResponse} from "@/lib/queries/reports.ts";
import {NewScheduledReportButton} from "@/pages/reports/schedule/NewScheduledReportButton.tsx";
import {bulkDeleteScheduledReports} from "@/pages/reports/schedule/quickActions.ts";
import {ScheduledReportActions} from "@/pages/reports/schedule/ScheduledReportActions.tsx";
import {ScheduledReportNameCell} from "@/pages/reports/schedule/ScheduledReportNameCell.tsx";

import styles from "./ScheduledReportsList.module.scss";

export const ScheduledReportsList = () => {
  const {user} = useCurrentUserContext();
  const {pagination, setPagination} = usePagination({useUrlParams: false, defaultLimit: 50});
  const {data} = useAutoThrowSuspenseQuery<ScheduleReportsResponse>(GQL_SCHEDULED_REPORTS, {
    variables: {
      ...pagination,
    },
  });
  const {items, total} = data.scheduledReports;
  const {rowSelection, setRowSelection} = useFilteredRowSelection<ScheduledReport>({
    data: items,
    getRowId: (report) => report.id.toString(),
  });
  const specs: ColumnSpec<ScheduledReport>[] = [
    {
      fieldName: "name",
      fieldTitle: "Name",
      meta: {headerClassName: styles.nameCell},
      accessor: ({name}) => (
        <Tooltip tooltipContent={name} delayMs={1000} onModal>
          <div className="ellipsis-overflow">{name}</div>
        </Tooltip>
      ),
    },
    {
      fieldName: "schedule_display",
      fieldTitle: "Schedule",
      meta: {headerClassName: styles.scheduleCell},
    },
    {
      fieldName: "report_name",
      fieldTitle: "Report",
      meta: {headerClassName: styles.reportCell},
      accessor: ({report}) => <ScheduledReportNameCell report={report} />,
    },
    {
      fieldName: "enabled",
      fieldTitle: "Enabled",
      meta: {headerClassName: styles.enabledCell},
      accessor: (obj) => <CheckMarkIcon enabled={obj.is_enabled} />,
    },
  ];
  if (user?.has_write_access) {
    specs.push({
      fieldName: "actions",
      fieldTitle: "",
      meta: {headerClassName: styles.actionCell},
      accessor: (obj) => <ScheduledReportActions id={obj.id} name={obj.name} />,
    });
  }
  return (
    <div>
      <Table
        fixedWidth
        minWidth={"800px"}
        rows={items}
        specs={specs}
        initialState={{columnPinning: {right: ["actions"]}}}
        getRowId={(report) => report.id.toString()}
        rowSelection={rowSelection}
        setRowSelection={user?.has_write_access ? setRowSelection : undefined}
        actionBar={
          user?.has_write_access && (
            <BulkDeleteCrownAction
              selectedItems={rowSelection}
              setSelectedItems={setRowSelection}
              title="Delete Scheduled Reports"
              itemLabel="Scheduled Report"
              onDelete={(ids) => bulkDeleteScheduledReports(ids.map(Number))}
            />
          )
        }
        noDataComponent={
          <TableEmptyStateCta
            colSpan={specs.length + (user?.has_write_access ? 1 : 0)}
            title="You do not have any Scheduled Reports."
            cta={
              user?.has_write_access ? (
                <NewScheduledReportButton text="Create Scheduled Report" variant="secondary" />
              ) : undefined
            }
          />
        }
        pagination={pagination}
        updatePagination={setPagination}
        totalRows={total}
      />
    </div>
  );
};
